/**
 * @element qds-date-picker
 *
 * Date and date-range entry: a text field that parses typed dates, plus a
 * calendar popover. Typing is the primary input method; the calendar is an aid.
 *
 * The value is always an ISO date string (YYYY-MM-DD), never a Date object.
 * A Date drags a timezone into a field that has no time, and an as-of date
 * that shifts a day in UTC is a reporting defect.
 *
 * @attr {string} value - ISO date string YYYY-MM-DD.
 * @attr {string} end-value - Range mode only; second date as YYYY-MM-DD.
 * @attr {boolean} range - Two dates in one field.
 * @attr {string} min - ISO date; days before are disabled.
 * @attr {string} max - ISO date; days after are disabled.
 * @attr {number} first-day-of-week - 0 = Sunday (locale default).
 * @attr {string} display-format - Pattern like MM/DD/YYYY, or locale short by default.
 * @attr {string} locale - BCP 47 locale tag.
 * @attr {boolean} show-week-numbers - Show ISO week numbers in a leading column.
 * @attr {boolean} open - Reflected; calendar popover visibility.
 * @attr {'auto'|'bottom'|'top'} placement - Popover placement preference.
 * @attr {string} name - Form field name.
 * @attr {string} label - Label text.
 * @attr {string} hint - Helper text below the field.
 * @attr {string} error-text - Error message shown when invalid.
 * @attr {'sm'|'md'|'lg'} size - Control size. Default "md".
 * @attr {boolean} disabled - Disables interaction.
 * @attr {boolean} readonly - Field holds value; no pointer or key response.
 * @attr {boolean} required - Must have a value (valueMissing when empty).
 * @attr {boolean} invalid - Author-forced invalid state.
 * @attr {boolean} clearable - Renders a clear button when non-empty.
 * @attr {'blur'|'input'|'submit'} validate-on - Validation timing. Default "blur".
 *
 * @property {(iso: string) => boolean} disabledDates - Predicate; matching days are disabled.
 * @property {{label: string, value: string}[]} shortcuts - Shortcut buttons in the calendar.
 * @property {() => Date} _todayFn - @internal Test hook for stubbing today's date.
 *
 * @csspart container - Outer wrapper.
 * @csspart label - Label text.
 * @csspart wrapper - Input wrapper (control surface).
 * @csspart input - The text input.
 * @csspart calendar-button - Calendar toggle button.
 * @csspart clear-button - Clear value button.
 * @csspart panel - Calendar popover panel.
 * @csspart panel-header - Calendar header (nav + label).
 * @csspart nav-prev - Previous navigation button.
 * @csspart nav-next - Next navigation button.
 * @csspart month-label - Month/year label (clickable for drill-down).
 * @csspart shortcut - A shortcut button.
 * @csspart weekday - A weekday header cell.
 * @csspart week-number - A week number cell.
 * @csspart day - A day cell.
 * @csspart day-today - Today's day cell.
 * @csspart day-selected - Selected day cell.
 * @csspart day-in-range - In-range day cell (range mode).
 * @csspart day-disabled - Disabled day cell.
 * @csspart month-grid - Month selection grid.
 * @csspart year-grid - Year selection grid.
 * @csspart hint - Helper text.
 * @csspart error - Error message.
 *
 * @fires change - Date committed. Detail: `{ component, value, endValue }`.
 * @fires input - Same moment as change. Detail: `{ component, value, endValue }`.
 * @fires qds-open - Calendar opened. Detail: `{ component }`.
 * @fires qds-close - Calendar closed. Detail: `{ component }`.
 * @fires qds-month-change - Visible month changed. Detail: `{ component, year, month }`.
 * @fires qds-validity-change - Validity transition. Detail: `QdsValidityChangeEventDetail`.
 *
 * @example
 * <qds-date-picker label="As-of date" value="2026-03-07"></qds-date-picker>
 * <qds-date-picker label="Period" range min="2026-01-01" max="2026-12-31"></qds-date-picker>
 */

import { loadBaseCSS } from '../shared/button-utils.js';
import {
  attachInternalsSafe,
  ValidityTracker,
  dispatchValidityChange,
  generateControlId,
  isAttrTruthy,
  reflectBoolean,
  parseSize,
  parseValidateOn,
  getFormControlStyles,
  upgradeProperty,
} from '../shared/form-control.js';
import { PopoverController } from '../shared/popover-utils.js';
import type { QdsControlSize, QdsValidateOn, QdsValidity, QdsValidityChangeEventDetail } from '../../types/index';

// ── Date utilities ──────────────────────────────────────────────────────

/** All date arithmetic uses local-time Date objects but converts back to
 *  ISO strings at the boundary. No Date object ever crosses the public API. */

function isoFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dateFromIso(iso: string): Date {
  const parts = iso.split('-');
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
}

function isMonthFirst(locale: string): boolean {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date(2026, 2, 7));
    const mi = parts.findIndex(p => p.type === 'month');
    const di = parts.findIndex(p => p.type === 'day');
    return mi < di;
  } catch {
    return true;
  }
}

function derivePattern(locale: string): string {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date(2026, 2, 7));
    return parts.map(p => {
      if (p.type === 'year') return 'YYYY';
      if (p.type === 'month') return 'MM';
      if (p.type === 'day') return 'DD';
      return p.value;
    }).join('');
  } catch {
    return 'MM/DD/YYYY';
  }
}

function formatDateIso(iso: string, locale: string, pattern: string): string {
  if (!iso) return '';
  try {
    const d = dateFromIso(iso);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d);
  } catch {
    const [y, m, day] = iso.split('-');
    return pattern.replace('YYYY', y).replace('MM', m).replace('DD', day);
  }
}

function formatPattern(iso: string, pattern: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return pattern.replace('YYYY', y).replace('MM', m).replace('DD', d);
}

function parseMonthName(name: string, locale: string): number {
  const lower = name.toLowerCase().replace(/\./g, '');
  for (let i = 0; i < 12; i++) {
    try {
      const mn = new Intl.DateTimeFormat(locale, { month: 'short' })
        .format(new Date(2026, i, 1)).toLowerCase().replace(/\./g, '');
      if (mn === lower || mn.startsWith(lower) || lower.startsWith(mn)) return i;
    } catch { /* ignore */ }
  }
  const en = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
              'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  for (let i = 0; i < 12; i++) {
    if (en[i] === lower || en[i].startsWith(lower)) return i;
  }
  return -1;
}

function isoWeekNumber(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function addDays(iso: string, n: number): string {
  const d = dateFromIso(iso);
  d.setDate(d.getDate() + n);
  return isoFromDate(d);
}

function addMonths(iso: string, n: number): string {
  const d = dateFromIso(iso);
  const targetDay = d.getDate();
  d.setMonth(d.getMonth() + n);
  // Handle month overflow (e.g. Jan 31 + 1 month = Mar 3, clamp to Feb 28/29)
  if (d.getDate() !== targetDay) {
    d.setDate(0);
  }
  return isoFromDate(d);
}

function addYears(iso: string, n: number): string {
  const d = dateFromIso(iso);
  d.setFullYear(d.getFullYear() + n);
  return isoFromDate(d);
}

function firstDayOfMonth(year: number, month: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-01`;
}

function lastDayOfMonth(year: number, month: number): string {
  const d = new Date(year, month + 1, 0);
  return isoFromDate(d);
}

function weekdayLabel(dayIdx: number, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { weekday: 'narrow' })
      .format(new Date(2026, 0, 4 + dayIdx));
  } catch {
    return ['S', 'M', 'T', 'W', 'T', 'F', 'S'][dayIdx];
  }
}

function weekdayShort(dayIdx: number, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { weekday: 'short' })
      .format(new Date(2026, 0, 4 + dayIdx));
  } catch {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayIdx];
  }
}

function monthLabelName(year: number, month: number, locale: string): string {
  try {
    const mn = new Intl.DateTimeFormat(locale, { month: 'long' })
      .format(new Date(year, month, 1));
    return `${mn} ${year}`;
  } catch {
    return `${month + 1}/${year}`;
  }
}

function monthShort(month: number, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { month: 'short' })
      .format(new Date(2026, month, 1));
  } catch {
    return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month];
  }
}

function fullDateName(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    }).format(dateFromIso(iso));
  } catch {
    return iso;
  }
}

/** Parses typed text into an ISO date string, or null if unparseable. */
function parseDateInput(text: string, locale: string, todayFn: () => Date): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  const today = todayFn();
  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  // ── Shorthand: t, t+N, t-N ────────────────────────────────────────
  if (lower === 't') return isoFromDate(t0);
  const tMatch = lower.match(/^t\s*([+-])\s*(\d+)$/);
  if (tMatch) {
    const d = new Date(t0);
    d.setDate(d.getDate() + (tMatch[1] === '+' ? 1 : -1) * parseInt(tMatch[2], 10));
    return isoFromDate(d);
  }

  // ── End of period: eom, eoq, eoy ──────────────────────────────────
  if (lower === 'eom') return lastDayOfMonth(t0.getFullYear(), t0.getMonth());
  if (lower === 'eoy') return lastDayOfMonth(t0.getFullYear(), 11);
  if (lower === 'eoq') {
    const qEnd = Math.floor(t0.getMonth() / 3) * 3 + 2;
    return lastDayOfMonth(t0.getFullYear(), qEnd);
  }

  // ── ISO: YYYY-MM-DD ───────────────────────────────────────────────
  const isoM = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoM) {
    const y = parseInt(isoM[1]), m = parseInt(isoM[2]) - 1, d = parseInt(isoM[3]);
    const date = new Date(y, m, d);
    if (date.getFullYear() === y && date.getMonth() === m && date.getDate() === d)
      return isoFromDate(date);
    return null;
  }

  // ── 8-digit: MMDDYYYY or DDMMYYYY ─────────────────────────────────
  const digM = trimmed.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (digM) {
    return parseNumeric(parseInt(digM[1]), parseInt(digM[2]), parseInt(digM[3]), locale);
  }

  // ── Numeric with separators: 3/7, 03/07/2026, 3/7/26 ──────────────
  const sepM = trimmed.match(/^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2,4}))?$/);
  if (sepM) {
    let year = sepM[3] ? parseInt(sepM[3]) : t0.getFullYear();
    if (sepM[3] && sepM[3].length === 2) year += year < 50 ? 2000 : 1900;
    return parseNumeric(parseInt(sepM[1]), parseInt(sepM[2]), year, locale);
  }

  // ── Day Month Year: 7 Mar 2026 ────────────────────────────────────
  const namedM = trimmed.match(/^(\d{1,2})\s+([a-z]{3,})\.?\s+(\d{4})$/i);
  if (namedM) {
    const mi = parseMonthName(namedM[2], locale);
    if (mi >= 0) {
      const d = parseInt(namedM[1]), y = parseInt(namedM[3]);
      const date = new Date(y, mi, d);
      if (date.getMonth() === mi && date.getDate() === d) return isoFromDate(date);
    }
    return null;
  }

  return null;
}

function parseNumeric(a: number, b: number, year: number, locale: string): string | null {
  const mf = isMonthFirst(locale);
  const m = mf ? a : b;
  const d = mf ? b : a;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const date = new Date(year, m - 1, d);
  if (date.getMonth() === m - 1 && date.getDate() === d) return isoFromDate(date);
  return null;
}

/** Parses a range string "start – end" into two ISO dates. */
function parseRangeInput(
  text: string, locale: string, todayFn: () => Date,
): { start: string | null; end: string | null } {
  const parts = text.split(/\s+[–-]\s+|\s+to\s+/i);
  if (parts.length === 2) {
    return {
      start: parseDateInput(parts[0], locale, todayFn),
      end: parseDateInput(parts[1], locale, todayFn),
    };
  }
  const single = parseDateInput(text, locale, todayFn);
  return { start: single, end: null };
}

// ── Types ───────────────────────────────────────────────────────────────

interface DatePickerChangeEventDetail {
  component: HTMLElement;
  value: string;
  endValue: string;
}

export interface QdsDatePickerShortcut {
  label: string;
  value: string;
}

type ViewLevel = 'days' | 'months' | 'years';

// ── Popover subclass for close callback ──────────────────────────────────

class DatePickerPopover extends PopoverController {
  onClose: (() => void) | null = null;

  hide(): void {
    const wasOpen = this.open;
    super.hide();
    if (wasOpen && this.onClose) this.onClose();
  }
}

// ── Component ────────────────────────────────────────────────────────────

export class QdsDatePicker extends HTMLElement {
  static formAssociated = true;
  static qdsFieldConsumer = true;

  static get observedAttributes(): string[] {
    return [
      'value', 'end-value', 'range', 'min', 'max',
      'first-day-of-week', 'display-format', 'locale',
      'show-week-numbers', 'open', 'placement',
      'name', 'label', 'hint', 'error-text', 'size',
      'disabled', 'readonly', 'required', 'invalid',
      'clearable', 'validate-on',
    ];
  }

  private _internals?: ElementInternals;
  private _validity: ValidityTracker;

  // Value state
  private _value = '';
  private _endValue = '';
  private _range = false;
  private _min = '';
  private _max = '';

  // Display state
  private _displayText = '';
  private _hasShownError = false;

  // Calendar state
  private _open = false;
  private _placement: 'auto' | 'bottom' | 'top' = 'auto';
  private _viewLevel: ViewLevel = 'days';
  private _viewYear = 2026;
  private _viewMonth = 0;
  private _focusedDay = '';
  private _rangeArmed = false;
  private _hoverEnd = '';

  // Configuration
  private _firstDayOfWeek = -1; // -1 = locale default
  private _displayFormat = '';
  private _locale = '';
  private _showWeekNumbers = false;
  private _disabledDatesFn?: (iso: string) => boolean;
  private _shortcuts: QdsDatePickerShortcut[] = [];

  // Form properties
  private _name = '';
  private _label = '';
  private _hint = '';
  private _errorText = '';
  private _size: QdsControlSize = 'md';
  private _disabled = false;
  private _formDisabled = false;
  private _readonly = false;
  private _required = false;
  private _invalid = false;
  private _clearable = false;
  private _validateOn: QdsValidateOn = 'blur';

  // Initial state for formResetCallback
  private _initialValue = '';
  private _initialEndValue = '';

  // IDs
  private _labelId: string;
  private _hintId: string;
  private _errorId: string;
  private _inputId: string;
  private _gridId: string;
  private _announceId: string;

  // Popover
  private _popover: DatePickerPopover | null = null;

  // Rendering flag to suppress blur during re-render
  private _rendering = false;

  /** @internal Test hook for stubbing today's date. */
  _todayFn: () => Date = () => new Date();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._internals = attachInternalsSafe(this);
    // jsdom may return a partial ElementInternals without validity/setFormValue;
    // treat it as unavailable so the fallback QdsValidity path is used.
    if (this._internals && (typeof this._internals.setFormValue !== 'function' || !this._internals.validity)) {
      this._internals = undefined;
    }
    this._validity = new ValidityTracker(this._internals);
    const c = generateControlId('dp');
    this._labelId = `${c}-label`;
    this._hintId = `${c}-hint`;
    this._errorId = `${c}-error`;
    this._inputId = `${c}-input`;
    this._gridId = `${c}-grid`;
    this._announceId = `${c}-announce`;
    loadBaseCSS();
  }

  // ── Properties ──────────────────────────────────────────────────────

  get value(): string { return this._value; }
  set value(v: string) {
    this._value = String(v);
    this._displayText = this._buildDisplayText();
    this._updateFormValue();
    this._updateValidity();
    if (this.isConnected) this.render();
  }

  get endValue(): string { return this._endValue; }
  set endValue(v: string) {
    this._endValue = String(v);
    this._displayText = this._buildDisplayText();
    this._updateFormValue();
    this._updateValidity();
    if (this.isConnected) this.render();
  }

  get range(): boolean { return this._range; }
  set range(v: boolean) {
    this._range = Boolean(v);
    reflectBoolean(this, 'range', this._range);
    this._displayText = this._buildDisplayText();
    this._updateFormValue();
    if (this.isConnected) this.render();
  }

  get min(): string { return this._min; }
  set min(v: string) {
    this._min = String(v);
    this.setAttribute('min', this._min);
    this._updateValidity();
    if (this.isConnected && this._open) this.render();
  }

  get max(): string { return this._max; }
  set max(v: string) {
    this._max = String(v);
    this.setAttribute('max', this._max);
    this._updateValidity();
    if (this.isConnected && this._open) this.render();
  }

  get firstDayOfWeek(): number { return this._firstDayOfWeek; }
  set firstDayOfWeek(v: number) {
    this._firstDayOfWeek = Number(v);
    this.setAttribute('first-day-of-week', String(v));
    if (this.isConnected && this._open) this.render();
  }

  get displayFormat(): string { return this._displayFormat; }
  set displayFormat(v: string) {
    this._displayFormat = String(v);
    this.setAttribute('display-format', this._displayFormat);
    this._displayText = this._buildDisplayText();
    if (this.isConnected) this.render();
  }

  get locale(): string { return this._locale; }
  set locale(v: string) {
    this._locale = String(v);
    this.setAttribute('locale', this._locale);
    this._displayText = this._buildDisplayText();
    if (this.isConnected) this.render();
  }

  get showWeekNumbers(): boolean { return this._showWeekNumbers; }
  set showWeekNumbers(v: boolean) {
    this._showWeekNumbers = Boolean(v);
    reflectBoolean(this, 'show-week-numbers', this._showWeekNumbers);
    if (this.isConnected && this._open) this.render();
  }

  get open(): boolean { return this._open; }
  set open(v: boolean) {
    if (Boolean(v) === this._open) return;
    if (v) this._openCalendar();
    else this._closeCalendar();
  }

  get placement(): 'auto' | 'bottom' | 'top' { return this._placement; }
  set placement(v: 'auto' | 'bottom' | 'top') {
    this._placement = v;
    this.setAttribute('placement', v);
  }

  get disabledDates(): ((iso: string) => boolean) | undefined { return this._disabledDatesFn; }
  set disabledDates(fn: ((iso: string) => boolean) | undefined) {
    this._disabledDatesFn = fn;
    if (this.isConnected && this._open) this.render();
  }

  get shortcuts(): QdsDatePickerShortcut[] { return this._shortcuts; }
  set shortcuts(v: QdsDatePickerShortcut[]) {
    this._shortcuts = Array.isArray(v) ? v : [];
    if (this.isConnected && this._open) this.render();
  }

  get name(): string { return this._name; }
  set name(v: string) {
    this._name = String(v);
    this.setAttribute('name', this._name);
    this._updateFormValue();
  }

  get label(): string { return this._label; }
  set label(v: string) {
    this._label = String(v);
    this.setAttribute('label', this._label);
    if (this.isConnected) this.render();
  }

  get hint(): string { return this._hint; }
  set hint(v: string) {
    this._hint = String(v);
    this.setAttribute('hint', this._hint);
    if (this.isConnected) this.render();
  }

  get errorText(): string { return this._errorText; }
  set errorText(v: string) {
    this._errorText = String(v);
    this.setAttribute('error-text', this._errorText);
    if (this.isConnected) this.render();
  }

  get size(): QdsControlSize { return this._size; }
  set size(v: QdsControlSize) {
    this._size = parseSize(v);
    this.setAttribute('size', this._size);
    if (this.isConnected) this.render();
  }

  get disabled(): boolean { return this._disabled; }
  set disabled(v: boolean) {
    reflectBoolean(this, 'disabled', v);
  }

  get readonly(): boolean { return this._readonly; }
  set readonly(v: boolean) {
    this._readonly = Boolean(v);
    reflectBoolean(this, 'readonly', this._readonly);
    if (this.isConnected) this.render();
  }

  get required(): boolean { return this._required; }
  set required(v: boolean) {
    this._required = Boolean(v);
    reflectBoolean(this, 'required', this._required);
    this._updateValidity();
    if (this.isConnected) this.render();
  }

  get invalid(): boolean { return this._invalid; }
  set invalid(v: boolean) {
    this._invalid = Boolean(v);
    reflectBoolean(this, 'invalid', this._invalid);
    if (this.isConnected) this.render();
  }

  get clearable(): boolean { return this._clearable; }
  set clearable(v: boolean) {
    this._clearable = Boolean(v);
    reflectBoolean(this, 'clearable', this._clearable);
    if (this.isConnected) this.render();
  }

  get validateOn(): QdsValidateOn { return this._validateOn; }
  set validateOn(v: QdsValidateOn) {
    this._validateOn = parseValidateOn(v);
    this.setAttribute('validate-on', this._validateOn);
  }

  get validity(): QdsValidity {
    return this._validity.validity;
  }

  // ── Lifecycle ───────────────────────────────────────────────────────

  connectedCallback(): void {
    if (!this.isConnected) return;

    upgradeProperty(this, 'disabledDates');
    upgradeProperty(this, 'shortcuts');
    this._value = this.getAttribute('value') || '';
    this._endValue = this.getAttribute('end-value') || '';
    this._range = isAttrTruthy(this.getAttribute('range'));
    this._min = this.getAttribute('min') || '';
    this._max = this.getAttribute('max') || '';
    this._firstDayOfWeek = this.hasAttribute('first-day-of-week')
      ? Number(this.getAttribute('first-day-of-week')) : -1;
    this._displayFormat = this.getAttribute('display-format') || '';
    this._locale = this.getAttribute('locale') || '';
    this._showWeekNumbers = isAttrTruthy(this.getAttribute('show-week-numbers'));
    this._open = isAttrTruthy(this.getAttribute('open'));
    this._placement = (this.getAttribute('placement') as 'auto' | 'bottom' | 'top') || 'auto';
    this._name = this.getAttribute('name') || '';
    this._label = this.getAttribute('label') || '';
    this._hint = this.getAttribute('hint') || '';
    this._errorText = this.getAttribute('error-text') || '';
    this._size = parseSize(this.getAttribute('size'));
    this._disabled = this._formDisabled || isAttrTruthy(this.getAttribute('disabled'));
    this._readonly = isAttrTruthy(this.getAttribute('readonly'));
    this._required = isAttrTruthy(this.getAttribute('required'));
    this._invalid = isAttrTruthy(this.getAttribute('invalid'));
    this._clearable = isAttrTruthy(this.getAttribute('clearable'));
    this._validateOn = parseValidateOn(this.getAttribute('validate-on'));

    this._initialValue = this._value;
    this._initialEndValue = this._endValue;
    this._displayText = this._buildDisplayText();

    this._updateFormValue();
    this._updateValidity();
    this.render();
  }

  disconnectedCallback(): void {
    this._removeListeners();
    if (this._popover) {
      this._popover.onClose = null;
      this._popover.destroy();
      this._popover = null;
    }
    if (this._open) {
      this._open = false;
      reflectBoolean(this, 'open', false);
    }
  }

  attributeChangedCallback(
    name: string, oldValue: string | null, newValue: string | null,
  ): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'value': this._value = newValue || ''; this._displayText = this._buildDisplayText(); this._updateFormValue(); this._updateValidity(); break;
      case 'end-value': this._endValue = newValue || ''; this._displayText = this._buildDisplayText(); this._updateFormValue(); this._updateValidity(); break;
      case 'range': this._range = newValue !== null && newValue !== 'false'; this._displayText = this._buildDisplayText(); this._updateFormValue(); break;
      case 'min': this._min = newValue || ''; this._updateValidity(); break;
      case 'max': this._max = newValue || ''; this._updateValidity(); break;
      case 'first-day-of-week': this._firstDayOfWeek = newValue != null ? Number(newValue) : -1; break;
      case 'display-format': this._displayFormat = newValue || ''; this._displayText = this._buildDisplayText(); break;
      case 'locale': this._locale = newValue || ''; this._displayText = this._buildDisplayText(); break;
      case 'show-week-numbers': this._showWeekNumbers = newValue !== null && newValue !== 'false'; break;
      case 'open':
        this._open = newValue !== null && newValue !== 'false';
        if (this._open) this._openCalendar();
        else this._closeCalendar();
        return; // open/close handles render
      case 'placement': this._placement = (newValue as 'auto' | 'bottom' | 'top') || 'auto'; break;
      case 'name': this._name = newValue || ''; this._updateFormValue(); break;
      case 'label': this._label = newValue || ''; break;
      case 'hint': this._hint = newValue || ''; break;
      case 'error-text': this._errorText = newValue || ''; break;
      case 'size': this._size = parseSize(newValue); break;
      case 'disabled': this._disabled = this._formDisabled || (newValue !== null && newValue !== 'false'); break;
      case 'readonly': this._readonly = newValue !== null && newValue !== 'false'; break;
      case 'required': this._required = newValue !== null && newValue !== 'false'; this._updateValidity(); break;
      case 'invalid': this._invalid = newValue !== null && newValue !== 'false'; break;
      case 'clearable': this._clearable = newValue !== null && newValue !== 'false'; break;
      case 'validate-on': this._validateOn = parseValidateOn(newValue); break;
    }

    if (this.isConnected) this.render();
  }

  // ── Form association ────────────────────────────────────────────────

  private _updateFormValue(): void {
    if (!this._internals) return;
    try {
      if (this._range) {
        if (this._value) {
          const fd = new FormData();
          fd.append(this._name || 'date-picker', this._value);
          if (this._endValue) fd.append(`${this._name || 'date-picker'}-end`, this._endValue);
          this._internals.setFormValue(fd);
        } else {
          this._internals.setFormValue(null);
        }
      } else {
        this._internals.setFormValue(this._value || null);
      }
    } catch {
      /* jsdom may not implement setFormValue */
    }
  }

  private _updateValidity(): void {
    const wasValid = this._validity.valid;
    const flags: Partial<{ valueMissing: boolean; typeMismatch: boolean; rangeUnderflow: boolean; rangeOverflow: boolean; customError: boolean; valid: boolean }> = {};

    if (this._required && !this._value) {
      flags.valueMissing = true;
      flags.valid = false;
      this._validity.setValidity(flags, 'Please enter a date.');
      if (wasValid !== this._validity.valid) dispatchValidityChange(this, this._validity);
      return;
    }

    if (this._value) {
      if (this._min && this._value < this._min) {
        flags.rangeUnderflow = true;
        flags.valid = false;
        this._validity.setValidity(flags, `Date must be on or after ${this._min}.`);
        if (wasValid !== this._validity.valid) dispatchValidityChange(this, this._validity);
        return;
      }
      if (this._max && this._value > this._max) {
        flags.rangeOverflow = true;
        flags.valid = false;
        this._validity.setValidity(flags, `Date must be on or before ${this._max}.`);
        if (wasValid !== this._validity.valid) dispatchValidityChange(this, this._validity);
        return;
      }
      if (this._disabledDatesFn && this._disabledDatesFn(this._value)) {
        flags.customError = true;
        flags.valid = false;
        this._validity.setValidity(flags, 'This date is not available.');
        if (wasValid !== this._validity.valid) dispatchValidityChange(this, this._validity);
        return;
      }
    }

    if (this._range && this._value && this._endValue && this._endValue < this._value) {
      flags.customError = true;
      flags.valid = false;
      this._validity.setValidity(flags, 'End date must be on or after the start date.');
      if (wasValid !== this._validity.valid) dispatchValidityChange(this, this._validity);
      return;
    }

    // All clear
    this._validity.setValidity({
      valueMissing: false, typeMismatch: false,
      rangeUnderflow: false, rangeOverflow: false,
      customError: false, valid: true,
    }, '');
    if (wasValid !== this._validity.valid) dispatchValidityChange(this, this._validity);
  }

  checkValidity(): boolean { return this._validity.checkValidity(); }
  reportValidity(): boolean { return this._validity.reportValidity(); }

  setCustomValidity(message: string): void {
    const wasValid = this._validity.valid;
    this._validity.setCustomValidity(message);
    if (wasValid !== this._validity.valid) dispatchValidityChange(this, this._validity);
  }

  formResetCallback(): void {
    this._value = this._initialValue;
    this._endValue = this._initialEndValue;
    this._rangeArmed = false;
    this._displayText = this._buildDisplayText();
    this._updateFormValue();
    this._updateValidity();
    this.render();
  }

  formDisabledCallback(d: boolean): void {
    this._formDisabled = d;
    this._disabled = d || isAttrTruthy(this.getAttribute('disabled'));
    if (this.isConnected) this.render();
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  private _effectiveLocale(): string {
    return this._locale || (typeof document !== 'undefined' ? document.documentElement.lang : '') || 'en-US';
  }

  private _effectiveFirstDay(): number {
    if (this._firstDayOfWeek >= 0) return this._firstDayOfWeek;
    try {
      const li = new Intl.Locale(this._effectiveLocale());
      if ('weekInfo' in li) {
        const wi = (li as unknown as { weekInfo: { firstDay: number } }).weekInfo;
        return wi.firstDay % 7;
      }
    } catch { /* ignore */ }
    return 0;
  }

  private _effectivePattern(): string {
    if (this._displayFormat) return this._displayFormat;
    return derivePattern(this._effectiveLocale());
  }

  private _formatDate(iso: string): string {
    if (!iso) return '';
    if (this._displayFormat) return formatPattern(iso, this._displayFormat);
    return formatDateIso(iso, this._effectiveLocale(), this._effectivePattern());
  }

  private _buildDisplayText(): string {
    if (this._range) {
      const s = this._value ? this._formatDate(this._value) : '';
      const e = this._endValue ? this._formatDate(this._endValue) : '';
      if (s && e) return `${s} – ${e}`;
      if (s) return `${s} –`;
      return '';
    }
    return this._value ? this._formatDate(this._value) : '';
  }

  private _placeholder(): string {
    return this._effectivePattern();
  }

  private _isDayDisabled(iso: string): boolean {
    if (this._min && iso < this._min) return true;
    if (this._max && iso > this._max) return true;
    if (this._disabledDatesFn && this._disabledDatesFn(iso)) return true;
    return false;
  }

  private _isInRange(iso: string): boolean {
    if (!this._range || !this._value) return false;
    const end = this._hoverEnd || this._endValue;
    if (!end) return false;
    if (end < this._value) return false;
    return iso >= this._value && iso <= end;
  }

  private _todayIso(): string {
    return isoFromDate(this._todayFn());
  }

  // ── Calendar control ────────────────────────────────────────────────

  private _openCalendar(): void {
    if (this._open || this._disabled || this._readonly) return;
    this._open = true;
    reflectBoolean(this, 'open', true);

    // Set focused day and view month
    const base = this._value || this._todayIso();
    const d = dateFromIso(base);
    this._focusedDay = base;
    this._viewYear = d.getFullYear();
    this._viewMonth = d.getMonth();
    this._viewLevel = 'days';
    this._rangeArmed = false;
    this._hoverEnd = '';

    this.dispatchEvent(new CustomEvent('qds-open', {
      bubbles: true, composed: true, detail: { component: this },
    }));

    this.render();

    this._dispatchMonthChange(this._viewYear, this._viewMonth + 1);
  }

  private _closeCalendar(): void {
    if (!this._open) return;
    this._open = false;
    reflectBoolean(this, 'open', false);
    this._rangeArmed = false;
    this._hoverEnd = '';

    if (this._popover) {
      this._popover.onClose = null;
      this._popover.hide();
      this._popover = null;
    }

    this.dispatchEvent(new CustomEvent('qds-close', {
      bubbles: true, composed: true, detail: { component: this },
    }));

    this.render();
  }

  private _toggleCalendar(): void {
    if (this._open) this._closeCalendar();
    else this._openCalendar();
  }

  private _onPopoverClosed(): void {
    if (this._open) {
      this._open = false;
      reflectBoolean(this, 'open', false);
      this._rangeArmed = false;
      this._hoverEnd = '';
      this._popover = null;
      this.dispatchEvent(new CustomEvent('qds-close', {
        bubbles: true, composed: true, detail: { component: this },
      }));
      this.render();
    }
  }

  // ── Calendar navigation ──────────────────────────────────────────────

  private _prevPage(): void {
    if (this._viewLevel === 'days') {
      const d = new Date(this._viewYear, this._viewMonth - 1, 1);
      this._viewYear = d.getFullYear();
      this._viewMonth = d.getMonth();
      this._dispatchMonthChange(this._viewYear, this._viewMonth + 1);
    } else if (this._viewLevel === 'months') {
      this._viewYear--;
    } else {
      this._viewYear -= 12;
    }
    this._renderCalendarOnly();
  }

  private _nextPage(): void {
    if (this._viewLevel === 'days') {
      const d = new Date(this._viewYear, this._viewMonth + 1, 1);
      this._viewYear = d.getFullYear();
      this._viewMonth = d.getMonth();
      this._dispatchMonthChange(this._viewYear, this._viewMonth + 1);
    } else if (this._viewLevel === 'months') {
      this._viewYear++;
    } else {
      this._viewYear += 12;
    }
    this._renderCalendarOnly();
  }

  private _drillUp(): void {
    if (this._viewLevel === 'days') {
      this._viewLevel = 'months';
    } else if (this._viewLevel === 'months') {
      this._viewLevel = 'years';
    }
    this._renderCalendarOnly();
  }

  private _selectMonth(month: number): void {
    this._viewMonth = month;
    this._viewLevel = 'days';
    this._dispatchMonthChange(this._viewYear, this._viewMonth + 1);
    this._renderCalendarOnly();
  }

  private _selectYear(year: number): void {
    this._viewYear = year;
    this._viewLevel = 'months';
    this._renderCalendarOnly();
  }

  // ── Day selection ───────────────────────────────────────────────────

  private _selectDay(iso: string): void {
    if (this._isDayDisabled(iso)) return;

    if (this._range) {
      if (!this._rangeArmed || !this._value) {
        // Set start, arm end
        this._value = iso;
        this._endValue = '';
        this._rangeArmed = true;
        this._hoverEnd = '';
        this._focusedDay = iso;
      } else {
        // Armed: set end
        if (iso < this._value) {
          // Before start: restart
          this._value = iso;
          this._endValue = '';
          this._rangeArmed = true;
          this._hoverEnd = '';
          this._focusedDay = iso;
        } else {
          // Valid end
          this._endValue = iso;
          this._rangeArmed = false;
          this._focusedDay = iso;
          this._closeCalendar();
        }
      }
    } else {
      this._value = iso;
      this._focusedDay = iso;
      this._closeCalendar();
    }

    this._displayText = this._buildDisplayText();
    this._updateFormValue();
    this._updateValidity();
    this._dispatchChange();
    if (this._open) this.render();
  }

  // ── Keyboard ────────────────────────────────────────────────────────

  private _onInputKeyDown(e: KeyboardEvent): void {
    if (this._disabled || this._readonly) return;

    if (!this._open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        this._openCalendar();
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        this._parseAndCommit();
      }
      return;
    }

    // Calendar open
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this._moveFocus(7);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._moveFocus(-7);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this._moveFocus(-1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        this._moveFocus(1);
        break;
      case 'PageDown':
        e.preventDefault();
        if (e.shiftKey) this._moveFocusYear(1);
        else this._moveFocusMonth(1);
        break;
      case 'PageUp':
        e.preventDefault();
        if (e.shiftKey) this._moveFocusYear(-1);
        else this._moveFocusMonth(-1);
        break;
      case 'Home':
        e.preventDefault();
        this._focusFirstOfMonth();
        break;
      case 'End':
        e.preventDefault();
        this._focusLastOfMonth();
        break;
      case 'Enter':
        e.preventDefault();
        this._selectDay(this._focusedDay);
        break;
      // Escape handled by PopoverController
    }
  }

  private _moveFocus(dayDelta: number): void {
    if (!this._focusedDay) return;
    this._focusedDay = addDays(this._focusedDay, dayDelta);
    this._syncViewToFocused();
    this._updateActiveDescendant();
    this._renderCalendarOnly();
  }

  private _moveFocusMonth(delta: number): void {
    if (!this._focusedDay) return;
    this._focusedDay = addMonths(this._focusedDay, delta);
    this._syncViewToFocused();
    this._updateActiveDescendant();
    this._renderCalendarOnly();
  }

  private _moveFocusYear(delta: number): void {
    if (!this._focusedDay) return;
    this._focusedDay = addYears(this._focusedDay, delta);
    this._syncViewToFocused();
    this._updateActiveDescendant();
    this._renderCalendarOnly();
  }

  private _focusFirstOfMonth(): void {
    if (!this._focusedDay) return;
    const d = dateFromIso(this._focusedDay);
    this._focusedDay = firstDayOfMonth(d.getFullYear(), d.getMonth());
    this._updateActiveDescendant();
    this._renderCalendarOnly();
  }

  private _focusLastOfMonth(): void {
    if (!this._focusedDay) return;
    const d = dateFromIso(this._focusedDay);
    this._focusedDay = lastDayOfMonth(d.getFullYear(), d.getMonth());
    this._updateActiveDescendant();
    this._renderCalendarOnly();
  }

  private _syncViewToFocused(): void {
    if (!this._focusedDay) return;
    const d = dateFromIso(this._focusedDay);
    if (d.getFullYear() !== this._viewYear || d.getMonth() !== this._viewMonth) {
      this._viewYear = d.getFullYear();
      this._viewMonth = d.getMonth();
      this._dispatchMonthChange(this._viewYear, this._viewMonth + 1);
    }
  }

  private _updateActiveDescendant(): void {
    if (!this.shadowRoot) return;
    const input = this.shadowRoot.querySelector(`#${this._inputId}`) as HTMLElement | null;
    const grid = this.shadowRoot.querySelector(`#${this._gridId}`) as HTMLElement | null;
    const cellId = this._focusedDay ? `day-${this._focusedDay}` : '';
    if (input) input.setAttribute('aria-activedescendant', cellId);
    if (grid) grid.setAttribute('aria-activedescendant', cellId);

    // Update aria-live announcement
    const announce = this.shadowRoot.querySelector(`#${this._announceId}`);
    if (announce) {
      announce.textContent = this._viewLevel === 'days'
        ? monthLabelName(this._viewYear, this._viewMonth, this._effectiveLocale())
        : String(this._viewYear);
    }
  }

  /** Re-renders only the calendar panel without rebuilding the field. */
  private _renderCalendarOnly(): void {
    if (!this.shadowRoot || !this._open) return;
    const panel = this.shadowRoot.querySelector('.dp-panel');
    if (!panel) { this.render(); return; }
    panel.innerHTML = this._buildCalendarInner();
    this._wireCalendarListeners();
    this._updateActiveDescendant();
  }

  // ── Parsing / committing ────────────────────────────────────────────

  private _parseAndCommit(): void {
    const input = this.shadowRoot?.querySelector(`#${this._inputId}`) as HTMLInputElement | null;
    if (!input) return;
    const text = input.value.trim();

    if (!text) {
      // Empty: clear value
      if (this._value || this._endValue) {
        this._value = '';
        this._endValue = '';
        this._displayText = '';
        this._updateFormValue();
        this._updateValidity();
        this._dispatchChange();
        this.render();
      }
      return;
    }

    const locale = this._effectiveLocale();

    if (this._range) {
      const { start, end } = parseRangeInput(text, locale, this._todayFn);
      if (start) {
        this._value = start;
        this._endValue = end || '';
        this._displayText = this._buildDisplayText();
        this._updateFormValue();
        this._updateValidity();
        this._dispatchChange();
      } else {
        // Unparseable
        this._setTypeError();
      }
    } else {
      const parsed = parseDateInput(text, locale, this._todayFn);
      if (parsed) {
        this._value = parsed;
        this._displayText = this._buildDisplayText();
        this._updateFormValue();
        this._updateValidity();
        this._dispatchChange();
      } else {
        this._setTypeError();
      }
    }

    this.render();
  }

  private _setTypeError(): void {
    const wasValid = this._validity.valid;
    this._validity.setValidity(
      { typeMismatch: true, valid: false },
      'Please enter a valid date.',
    );
    if (wasValid !== this._validity.valid) dispatchValidityChange(this, this._validity);
    this._hasShownError = true;
    // Re-render to show error, but keep the typed text
    // Don't overwrite displayText so user can see what they typed
  }

  // ── Validation display ──────────────────────────────────────────────

  private _shouldShowError(): boolean {
    if (this._invalid) return true;
    if (this._validateOn === 'submit') return false;
    if (!this._hasShownError && this._validateOn === 'blur') return false;
    return !this._validity.valid;
  }

  private _onInputInput(e: Event): void {
    const input = e.target as HTMLInputElement;
    this._displayText = input.value;

    // Live validation after first error
    if (this._hasShownError && this._validateOn !== 'submit') {
      const locale = this._effectiveLocale();
      const parsed = this._range
        ? parseRangeInput(input.value, locale, this._todayFn)
        : { start: parseDateInput(input.value, locale, this._todayFn), end: null };

      const wasValid = this._validity.valid;
      if (parsed.start) {
        this._validity.setValidity(
          { typeMismatch: false, valid: !this._required || true }, '',
        );
        // Re-check other constraints
        if (this._required && !parsed.start) {
          this._validity.setValidity({ valueMissing: true, valid: false }, 'Please enter a date.');
        } else {
          this._validity.setValidity({ valueMissing: false, typeMismatch: false, valid: true }, '');
        }
      } else if (input.value.trim()) {
        this._validity.setValidity(
          { typeMismatch: true, valid: false }, 'Please enter a valid date.',
        );
      }
      if (wasValid !== this._validity.valid) dispatchValidityChange(this, this._validity);
    }

    // Dispatch input event
    this.dispatchEvent(new CustomEvent('input', {
      bubbles: true, composed: true,
      detail: { component: this, value: this._value, endValue: this._endValue },
    }));
  }

  private _onInputBlur(e?: FocusEvent): void {
    if (this._rendering) return;
    // Focus moving to another element within this component (nav buttons,
    // month/year drill-down, shortcuts, day cells) is not a dismissal —
    // only close when focus actually leaves the shadow root.
    const related = e?.relatedTarget as Node | null;
    const staysInComponent = !!(related && this.shadowRoot?.contains(related));
    this._parseAndCommit();
    this._hasShownError = true;
    if (this._open && !staysInComponent) this._closeCalendar();
  }

  // ── Event dispatch ──────────────────────────────────────────────────

  private _dispatchChange(): void {
    const detail: DatePickerChangeEventDetail = {
      component: this,
      value: this._value,
      endValue: this._endValue,
    };
    this.dispatchEvent(new CustomEvent('input', {
      bubbles: true, composed: true, detail,
    }));
    this.dispatchEvent(new CustomEvent('change', {
      bubbles: true, composed: true, detail,
    }));
  }

  private _dispatchMonthChange(year: number, month: number): void {
    this.dispatchEvent(new CustomEvent('qds-month-change', {
      bubbles: true, composed: true,
      detail: { component: this, year, month },
    }));
  }

  // ── Listener management ─────────────────────────────────────────────

  private _removeListeners(): void {
    // Shadow DOM listeners are cleaned up by innerHTML replacement.
    // No host-level listeners to remove (all on shadow elements).
  }

  // ── HTML building ───────────────────────────────────────────────────

  private _escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private _buildFieldHTML(): string {
    const showLabel = this._label !== '';
    const showHint = this._hint !== '' && !this._shouldShowError();
    const showError = this._shouldShowError() &&
      (this._errorText || this._validity.message) !== '';
    const showClear = this._clearable && (this._value || this._endValue) && !this._disabled && !this._readonly;
    const errorText = this._errorText || this._validity.message;

    const describeBy: string[] = [];
    if (showHint) describeBy.push(this._hintId);
    if (showError) describeBy.push(this._errorId);

    const ariaExpanded = this._open ? 'true' : 'false';
    const ariaInvalid = this._shouldShowError() ? 'true' : 'false';
    const ariaRequired = this._required ? 'true' : 'false';
    const inputVal = this._escapeHtml(this._displayText);
    const placeholder = this._escapeHtml(this._placeholder());
    const disabledAttr = this._disabled ? ' disabled' : '';
    const readonlyAttr = this._readonly ? ' readonly' : '';

    return `
      <div part="container" class="dp-container">
        ${showLabel ? `<label part="label" class="qds-label" for="${this._inputId}" id="${this._labelId}">${this._escapeHtml(this._label)}${this._required ? ' <span class="qds-required-marker" aria-hidden="true">*</span>' : ''}</label>` : ''}
        <div part="wrapper" class="qds-control-surface dp-wrapper" id="dp-wrapper">
          <input part="input" class="dp-input" id="${this._inputId}"
            type="text"
            role="combobox"
            aria-haspopup="dialog"
            aria-expanded="${ariaExpanded}"
            aria-controls="${this._gridId}"
            aria-invalid="${ariaInvalid}"
            aria-required="${ariaRequired}"
            ${describeBy.length ? `aria-describedby="${describeBy.join(' ')}"` : ''}
            ${this._label ? `aria-labelledby="${this._labelId}"` : ''}
            placeholder="${placeholder}"
            value="${inputVal}"
            autocomplete="off"
            ${disabledAttr}${readonlyAttr}
          />
          ${!this._readonly ? `<button part="calendar-button" class="dp-cal-btn" id="dp-cal-btn" aria-label="Open calendar"${disabledAttr} tabindex="-1">
            <svg viewBox="0 0 16 16" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" stroke-width="1.3"/>
              <path d="M2 6.5H14" stroke="currentColor" stroke-width="1.3"/>
              <path d="M5 1.5V4M11 1.5V4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
            </svg>
          </button>` : ''}
          ${showClear ? `<button part="clear-button" class="dp-clear-btn" id="dp-clear-btn" aria-label="Clear ${this._escapeHtml(this._label || 'date')}" tabindex="-1">
            <svg viewBox="0 0 16 16" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>` : ''}
        </div>
        ${this._open ? this._buildPanelHTML() : ''}
        ${showHint ? `<div part="hint" class="qds-hint" id="${this._hintId}">${this._escapeHtml(this._hint)}</div>` : ''}
        ${showError ? `<div part="error" class="qds-error" id="${this._errorId}" role="alert">${this._escapeHtml(errorText)}</div>` : ''}
        <div class="dp-sr-only" id="${this._announceId}" aria-live="polite"></div>
      </div>
    `;
  }

  private _buildPanelHTML(): string {
    return `<div part="panel" class="dp-panel" role="dialog" aria-modal="false" aria-label="${this._escapeHtml(monthLabelName(this._viewYear, this._viewMonth, this._effectiveLocale()))}">${this._buildCalendarInner()}</div>`;
  }

  private _buildCalendarInner(): string {
    if (this._viewLevel === 'months') return this._buildMonthGrid();
    if (this._viewLevel === 'years') return this._buildYearGrid();
    return this._buildDayView();
  }

  private _buildDayView(): string {
    const locale = this._effectiveLocale();
    const firstDay = this._effectiveFirstDay();
    const twoMonths = this._range;
    const monthCount = twoMonths ? 2 : 1;

    let monthsHTML = '';
    for (let mi = 0; mi < monthCount; mi++) {
      const d = new Date(this._viewYear, this._viewMonth + mi, 1);
      const y = d.getFullYear();
      const m = d.getMonth();
      monthsHTML += this._buildSingleMonth(y, m, locale, firstDay, mi);
    }

    const shortcutsHTML = this._buildShortcuts();

    return `
      ${twoMonths ? `<div class="dp-months-wrapper">${monthsHTML}</div>` : monthsHTML}
      ${shortcutsHTML}
    `;
  }

  private _buildSingleMonth(year: number, month: number, locale: string, firstDay: number, idx: number): string {
    const label = monthLabelName(year, month, locale);
    const showNavPrev = idx === 0;
    const showNavNext = idx === monthCount_ref(this) - 1;

    // Weekday headers
    const headers: string[] = [];
    if (this._showWeekNumbers) headers.push('<span class="dp-week-num-hd"></span>');
    for (let i = 0; i < 7; i++) {
      const dayIdx = (firstDay + i) % 7;
      const narrow = weekdayLabel(dayIdx, locale);
      const full = weekdayShort(dayIdx, locale);
      headers.push(`<span role="columnheader" part="weekday" class="dp-weekday" abbr="${this._escapeHtml(full)}">${this._escapeHtml(narrow)}</span>`);
    }

    // Day cells
    const firstOfMonth = new Date(year, month, 1);
    const offset = (firstOfMonth.getDay() - firstDay + 7) % 7;
    const startCell = new Date(year, month, 1 - offset);

    const todayIso = this._todayIso();
    const weeks: string[] = [];
    for (let w = 0; w < 6; w++) {
      const cells: string[] = [];
      const weekDate = new Date(startCell);
      weekDate.setDate(startCell.getDate() + w * 7);

      if (this._showWeekNumbers) {
        const wn = isoWeekNumber(isoFromDate(weekDate));
        cells.push(`<span class="dp-week-number" part="week-number">${wn}</span>`);
      }

      for (let dd = 0; dd < 7; dd++) {
        const cellDate = new Date(weekDate);
        cellDate.setDate(weekDate.getDate() + dd);
        const iso = isoFromDate(cellDate);
        cells.push(this._buildDayCell(cellDate, iso, month, todayIso));
      }
      weeks.push(`<div role="row" class="dp-row">${cells.join('')}</div>`);
    }

    return `
      <div class="dp-month-col">
        <div part="panel-header" class="dp-panel-header">
          ${showNavPrev ? `<button part="nav-prev" class="dp-nav dp-nav-prev" id="dp-nav-prev" aria-label="Previous month"><svg viewBox="0 0 16 16" width="14" height="14" fill="none"><path d="M10 3L5 8L10 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>` : '<span class="dp-nav-spacer"></span>'}
          <button part="month-label" class="dp-month-label" id="dp-month-label-${idx}" aria-label="${this._escapeHtml(label)}, click to change month">${this._escapeHtml(label)}</button>
          ${showNavNext ? `<button part="nav-next" class="dp-nav dp-nav-next" id="dp-nav-next" aria-label="Next month"><svg viewBox="0 0 16 16" width="14" height="14" fill="none"><path d="M6 3L11 8L6 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>` : '<span class="dp-nav-spacer"></span>'}
        </div>
        <div role="grid" class="dp-grid" id="${idx === 0 ? this._gridId : this._gridId + '-2'}" aria-activedescendant="${this._focusedDay ? 'day-' + this._focusedDay : ''}">
          <div role="row" class="dp-row">${headers.join('')}</div>
          ${weeks.join('')}
        </div>
      </div>
    `;
  }

  private _buildDayCell(date: Date, iso: string, viewMonth: number, todayIso: string): string {
    const isOutside = date.getMonth() !== viewMonth;
    const isToday = iso === todayIso;
    const isSelected = iso === this._value || (this._range && iso === this._endValue);
    const isStart = iso === this._value && this._range;
    const isEnd = iso === this._endValue && this._range;
    const isInRange = this._isInRange(iso) && !isStart && !isEnd;
    const isDisabled = this._isDayDisabled(iso);
    const isFocused = iso === this._focusedDay;

    const classes: string[] = ['dp-day'];
    if (isOutside) classes.push('outside');
    if (isToday) classes.push('today');
    if (isSelected) classes.push('selected');
    if (isStart) classes.push('range-start');
    if (isEnd) classes.push('range-end');
    if (isInRange) classes.push('in-range');
    if (isDisabled) classes.push('disabled');

    const parts: string[] = ['day'];
    if (isToday) parts.push('day-today');
    if (isSelected) parts.push('day-selected');
    if (isInRange) parts.push('day-in-range');
    if (isDisabled) parts.push('day-disabled');

    const ariaLabel = fullDateName(iso, this._effectiveLocale());
    const ariaSelected = isSelected ? 'true' : 'false';
    const ariaDisabled = isDisabled ? 'true' : 'false';

    return `<button role="gridcell" id="day-${iso}" data-iso="${iso}" class="${classes.join(' ')}" part="${parts.join(' ')}" aria-label="${this._escapeHtml(ariaLabel)}" aria-selected="${ariaSelected}" aria-disabled="${ariaDisabled}"${isDisabled ? ' disabled' : ''}${isFocused ? ' data-focused="true"' : ''}>${date.getDate()}</button>`;
  }

  private _buildMonthGrid(): string {
    const locale = this._effectiveLocale();
    const cells: string[] = [];
    for (let m = 0; m < 12; m++) {
      const isCurrent = m === this._viewMonth;
      cells.push(`<button role="gridcell" class="dp-grid-cell${isCurrent ? ' selected' : ''}" part="month-grid" data-month="${m}" aria-label="${this._escapeHtml(monthLabelName(this._viewYear, m, locale))}" aria-selected="${isCurrent ? 'true' : 'false'}">${this._escapeHtml(monthShort(m, locale))}</button>`);
    }

    return `
      <div part="panel-header" class="dp-panel-header">
        <button part="nav-prev" class="dp-nav dp-nav-prev" id="dp-nav-prev" aria-label="Previous year"><svg viewBox="0 0 16 16" width="14" height="14" fill="none"><path d="M10 3L5 8L10 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
        <button part="month-label" class="dp-month-label" id="dp-year-label" aria-label="${this._viewYear}, click to change year">${this._viewYear}</button>
        <button part="nav-next" class="dp-nav dp-nav-next" id="dp-nav-next" aria-label="Next year"><svg viewBox="0 0 16 16" width="14" height="14" fill="none"><path d="M6 3L11 8L6 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
      </div>
      <div class="dp-month-grid" role="grid">${cells.join('')}</div>
    `;
  }

  private _buildYearGrid(): string {
    const startYear = this._viewYear - 5;
    const cells: string[] = [];
    for (let i = 0; i < 12; i++) {
      const y = startYear + i;
      const isCurrent = y === this._viewYear;
      cells.push(`<button role="gridcell" class="dp-grid-cell${isCurrent ? ' selected' : ''}" part="year-grid" data-year="${y}" aria-selected="${isCurrent ? 'true' : 'false'}">${y}</button>`);
    }

    return `
      <div part="panel-header" class="dp-panel-header">
        <button part="nav-prev" class="dp-nav dp-nav-prev" id="dp-nav-prev" aria-label="Previous years"><svg viewBox="0 0 16 16" width="14" height="14" fill="none"><path d="M10 3L5 8L10 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
        <span part="month-label" class="dp-month-label">${startYear} – ${startYear + 11}</span>
        <button part="nav-next" class="dp-nav dp-nav-next" id="dp-nav-next" aria-label="Next years"><svg viewBox="0 0 16 16" width="14" height="14" fill="none"><path d="M6 3L11 8L6 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
      </div>
      <div class="dp-year-grid" role="grid">${cells.join('')}</div>
    `;
  }

  private _buildShortcuts(): string {
    const btns: string[] = [];
    // "Today" shortcut always shown
    btns.push(`<button part="shortcut" class="dp-shortcut" data-action="today">Today</button>`);
    for (const s of this._shortcuts) {
      btns.push(`<button part="shortcut" class="dp-shortcut" data-value="${this._escapeHtml(s.value)}">${this._escapeHtml(s.label)}</button>`);
    }
    return `<div class="dp-shortcuts">${btns.join('')}</div>`;
  }

  // ── CSS ─────────────────────────────────────────────────────────────

  private _buildStyles(): string {
    return `
      <style>
        ${getFormControlStyles()}

        .dp-container { display: inline-block; min-width: 200px; }

        .dp-wrapper { position: relative; gap: var(--theme-space-1, 4px); }
        .dp-input {
          flex: 1; border: none; background: transparent; outline: none;
          font-size: inherit; font-family: inherit; color: inherit;
          padding: 0; min-width: 0; height: 100%; box-sizing: border-box;
        }
        .dp-input::placeholder { color: var(--theme-text-tertiary, #6A6A6A); }

        .dp-cal-btn, .dp-clear-btn {
          flex-shrink: 0; display: flex; align-items: center; justify-content: center;
          background: transparent; border: none; cursor: pointer;
          padding: var(--theme-space-1, 4px); border-radius: var(--theme-radius-sm, 4px);
          color: var(--theme-text-secondary, #555);
          transition: background-color var(--transition-speed-fast, 0.15s);
        }
        .dp-cal-btn:hover, .dp-clear-btn:hover {
          background: var(--button-hover-bg, rgba(0,0,0,0.05));
        }
        .dp-cal-btn:disabled, .dp-clear-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        .dp-panel {
          position: absolute; left: 0;
          background: var(--theme-background-overlay, #fff);
          border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-md, 8px);
          box-shadow: var(--theme-shadow-md, 0 8px 24px rgba(0,0,0,0.12));
          padding: var(--theme-space-3, 12px);
          z-index: var(--qds-popover-z, 1000);
          box-sizing: border-box;
          margin-top: var(--theme-space-1, 4px);
        }

        .dp-panel-header {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: var(--theme-space-2, 8px);
        }

        .dp-nav {
          display: flex; align-items: center; justify-content: center;
          background: transparent; border: none; cursor: pointer;
          padding: var(--theme-space-1, 4px); border-radius: var(--theme-radius-sm, 4px);
          color: var(--theme-text-secondary, #555);
          transition: background-color var(--transition-speed-fast, 0.15s);
        }
        .dp-nav:hover { background: var(--button-hover-bg, rgba(0,0,0,0.05)); }
        .dp-nav-spacer { width: 24px; }

        .dp-month-label {
          background: transparent; border: none; cursor: pointer;
          font-size: 14px; font-weight: 600;
          color: var(--theme-text-primary, #333);
          padding: var(--theme-space-1, 4px) var(--theme-space-2, 8px);
          border-radius: var(--theme-radius-sm, 4px);
          transition: background-color var(--transition-speed-fast, 0.15s);
        }
        .dp-month-label:hover { background: var(--button-hover-bg, rgba(0,0,0,0.05)); }

        .dp-grid, .dp-month-col [role="grid"] {
          display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px;
        }
        .dp-row {
          display: contents;
        }

        .dp-weekday {
          text-align: center; font-size: 12px; font-weight: 500;
          color: var(--theme-text-tertiary, #6A6A6A); padding: 4px 0;
        }

        .dp-week-num-hd { width: 28px; }
        .dp-week-number {
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; color: var(--theme-text-tertiary, #6A6A6A);
          width: 28px; padding: 0 2px;
        }

        .dp-day {
          display: flex; align-items: center; justify-content: center;
          width: 36px; height: 36px; border: none; background: transparent;
          cursor: pointer; border-radius: var(--theme-radius-sm, 4px);
          font-size: 14px; color: var(--theme-text-primary, #333);
          padding: 0; font-family: inherit;
          transition: background-color var(--transition-speed-fast, 0.15s),
                      color var(--transition-speed-fast, 0.15s);
        }
        .dp-day:hover { background: var(--button-hover-bg, rgba(0,0,0,0.05)); }
        .dp-day.outside { color: var(--theme-text-tertiary, #6A6A6A); }
        .dp-day.today { box-shadow: inset 0 0 0 1px var(--theme-accent-primary, #445E89); }
        .dp-day.selected {
          background: var(--theme-accent-primary, #445E89);
          color: var(--theme-text-on-accent-primary, #fff);
        }
        .dp-day.in-range {
          background: var(--theme-background-accent-subtle, rgba(68,94,137,0.1));
          border-radius: 0;
        }
        .dp-day.range-start { border-radius: var(--theme-radius-sm, 4px) 0 0 var(--theme-radius-sm, 4px); }
        .dp-day.range-end { border-radius: 0 var(--theme-radius-sm, 4px) var(--theme-radius-sm, 4px) 0; }
        .dp-day.range-start.range-end { border-radius: var(--theme-radius-sm, 4px); }
        .dp-day.disabled { opacity: 0.4; pointer-events: none; }
        .dp-day[data-focused="true"] {
          outline: 2px solid var(--theme-accent-primary, #445E89);
          outline-offset: -2px;
        }

        .dp-months-wrapper { display: flex; flex-direction: column; gap: var(--theme-space-3, 12px); }
        @media (min-width: 600px) {
          .dp-months-wrapper { flex-direction: row; }
          .dp-month-col { flex: 0 0 auto; }
        }

        .dp-month-grid, .dp-year-grid {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--theme-space-1, 4px);
        }
        .dp-grid-cell {
          display: flex; align-items: center; justify-content: center;
          height: 40px; border: 1px solid transparent; background: transparent;
          cursor: pointer; border-radius: var(--theme-radius-sm, 4px);
          font-size: 14px; color: var(--theme-text-primary, #333);
          padding: 0; font-family: inherit;
          transition: background-color var(--transition-speed-fast, 0.15s);
        }
        .dp-grid-cell:hover { background: var(--button-hover-bg, rgba(0,0,0,0.05)); }
        .dp-grid-cell.selected {
          background: var(--theme-accent-primary, #445E89);
          color: var(--theme-text-on-accent-primary, #fff);
        }

        .dp-shortcuts { display: flex; flex-wrap: wrap; gap: var(--theme-space-1, 4px); margin-top: var(--theme-space-2, 8px); }
        .dp-shortcut {
          background: transparent; border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-sm, 4px);
          padding: var(--theme-space-1, 4px) var(--theme-space-3, 12px);
          cursor: pointer; font-size: 13px; color: var(--theme-text-secondary, #555);
          font-family: inherit;
          transition: background-color var(--transition-speed-fast, 0.15s);
        }
        .dp-shortcut:hover { background: var(--button-hover-bg, rgba(0,0,0,0.05)); }

        .dp-sr-only {
          position: absolute; width: 1px; height: 1px; overflow: hidden;
          clip: rect(0,0,0,0); white-space: nowrap;
        }

        :host([size="sm"]) .dp-day { width: 32px; height: 32px; font-size: 13px; }
        :host([size="lg"]) .dp-day { width: 40px; height: 40px; font-size: 15px; }
      </style>
    `;
  }

  // ── Wire up listeners ───────────────────────────────────────────────

  private _wireListeners(): void {
    if (!this.shadowRoot) return;
    const root = this.shadowRoot;

    const input = root.querySelector(`#${this._inputId}`);
    if (input) {
      input.addEventListener('keydown', (e: Event) => this._onInputKeyDown(e as KeyboardEvent));
      input.addEventListener('input', (e: Event) => this._onInputInput(e));
      input.addEventListener('blur', (e: Event) => this._onInputBlur(e as FocusEvent));
    }

    const calBtn = root.querySelector('#dp-cal-btn');
    if (calBtn) {
      calBtn.addEventListener('click', (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        this._toggleCalendar();
      });
    }

    const clearBtn = root.querySelector('#dp-clear-btn');
    if (clearBtn) {
      clearBtn.addEventListener('click', (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        this._value = '';
        this._endValue = '';
        this._displayText = '';
        this._updateFormValue();
        this._updateValidity();
        this.dispatchEvent(new CustomEvent('qds-clear', {
          bubbles: true, composed: true, detail: { component: this },
        }));
        this._dispatchChange();
        this.render();
      });
    }

    if (this._open) {
      // Panel mousedown listener is wired here (not in _wireCalendarListeners)
      // to avoid duplicates when _renderCalendarOnly re-wires inner elements.
      const panel = root.querySelector('.dp-panel');
      if (panel) {
        panel.addEventListener('mousedown', (e: Event) => {
          // e.target is the innermost element under the pointer — for a nav
          // button with an SVG icon, that's the <svg> or <path>, not the
          // <button> itself. Checking tagName directly against the target
          // misses those clicks, letting the browser's default mousedown
          // focus-shift blur the input and close/re-render the calendar
          // before the click that would have landed on the button ever
          // completes. Walk up to the nearest button/day-cell instead.
          const tgt = e.target as HTMLElement;
          if (tgt.closest('button') || tgt.closest('[data-iso]')) {
            e.preventDefault();
          }
        });
      }
      this._wireCalendarListeners();
    }
  }

  private _wireCalendarListeners(): void {
    if (!this.shadowRoot) return;
    const root = this.shadowRoot;

    // Nav buttons
    const navPrev = root.querySelector('#dp-nav-prev');
    if (navPrev) navPrev.addEventListener('click', (e: Event) => { e.preventDefault(); this._prevPage(); });
    const navNext = root.querySelector('#dp-nav-next');
    if (navNext) navNext.addEventListener('click', (e: Event) => { e.preventDefault(); this._nextPage(); });

    // Month label drill-down
    const monthLabel = root.querySelector('#dp-month-label-0') || root.querySelector('#dp-year-label');
    if (monthLabel) monthLabel.addEventListener('click', (e: Event) => { e.preventDefault(); this._drillUp(); });

    // Day cells
    root.querySelectorAll('[data-iso]').forEach(cell => {
      cell.addEventListener('click', (e: Event) => {
        e.preventDefault();
        const iso = (e.currentTarget as HTMLElement).getAttribute('data-iso');
        if (iso) this._selectDay(iso);
      });
      // Hover preview for range
      if (this._range && this._rangeArmed) {
        cell.addEventListener('mouseenter', (e: Event) => {
          const iso = (e.currentTarget as HTMLElement).getAttribute('data-iso');
          if (iso) this._onDayHover(iso);
        });
      }
    });

    // Month cells
    root.querySelectorAll('[data-month]').forEach(cell => {
      cell.addEventListener('click', (e: Event) => {
        e.preventDefault();
        const m = parseInt((e.currentTarget as HTMLElement).getAttribute('data-month') || '0', 10);
        this._selectMonth(m);
      });
    });

    // Year cells
    root.querySelectorAll('[data-year]').forEach(cell => {
      cell.addEventListener('click', (e: Event) => {
        e.preventDefault();
        const y = parseInt((e.currentTarget as HTMLElement).getAttribute('data-year') || '0', 10);
        this._selectYear(y);
      });
    });

    // Shortcuts
    root.querySelectorAll('.dp-shortcut').forEach(btn => {
      btn.addEventListener('click', (e: Event) => {
        e.preventDefault();
        const el = e.currentTarget as HTMLElement;
        const action = el.getAttribute('data-action');
        const val = el.getAttribute('data-value');
        if (action === 'today') {
          this._selectDay(this._todayIso());
        } else if (val) {
          this._onShortcutClick(val);
        }
      });
    });
  }

  private _onDayHover(iso: string): void {
    if (!this._range || !this._rangeArmed) return;
    this._hoverEnd = iso;
    this._updateRangePreview();
  }

  private _updateRangePreview(): void {
    if (!this.shadowRoot) return;
    const cells = this.shadowRoot.querySelectorAll('[data-iso]');
    cells.forEach(cell => {
      const el = cell as HTMLElement;
      const iso = el.getAttribute('data-iso');
      if (!iso) return;
      const inRange = this._isInRange(iso);
      el.classList.toggle('in-range', inRange && iso !== this._value && iso !== this._endValue);
    });
  }

  private _onShortcutClick(rawValue: string): void {
    if (this._range && rawValue.includes(',')) {
      const [start, end] = rawValue.split(',');
      this._value = start.trim();
      this._endValue = end.trim();
      this._rangeArmed = false;
      this._closeCalendar();
    } else if (this._range) {
      this._value = rawValue;
      this._endValue = '';
      this._rangeArmed = true;
    } else {
      this._value = rawValue;
      this._closeCalendar();
    }
    this._displayText = this._buildDisplayText();
    this._updateFormValue();
    this._updateValidity();
    this._dispatchChange();
    this.render();
  }

  // ── Render ──────────────────────────────────────────────────────────

  private render(): void {
    if (!this.shadowRoot) return;
    this._rendering = true;

    // Clean up old popover
    if (this._popover) {
      this._popover.onClose = null;
      this._popover.hide();
      this._popover = null;
    }

    this.shadowRoot.innerHTML = `${this._buildStyles()}${this._buildFieldHTML()}`;

    this._wireListeners();

    // Create popover if open
    if (this._open) {
      const wrapper = this.shadowRoot.querySelector('.dp-wrapper') as HTMLElement | null;
      const panel = this.shadowRoot.querySelector('.dp-panel') as HTMLElement | null;
      if (wrapper && panel) {
        const pc = new DatePickerPopover(this, wrapper, panel);
        pc.onClose = () => this._onPopoverClosed();
        this._popover = pc;
        pc.show();

        // Apply preferred placement
        if (this._placement === 'top') {
          panel.style.top = '';
          panel.style.bottom = `${wrapper.offsetHeight}px`;
        } else if (this._placement === 'bottom') {
          panel.style.bottom = '';
          panel.style.top = `${wrapper.offsetHeight}px`;
        }

        this._updateActiveDescendant();

        // Restore focus to input
        const input = this.shadowRoot.querySelector(`#${this._inputId}`) as HTMLElement | null;
        if (input) input.focus();
      }
    }

    this._rendering = false;
  }
}

/** Helper to get month count for range rendering. */
function monthCount_ref(host: QdsDatePicker): number {
  return host.range ? 2 : 1;
}

if (!customElements.get('qds-date-picker')) {
  customElements.define('qds-date-picker', QdsDatePicker);
}
